import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Mic, Search, Radio, User, Send, Bell, BellOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import {
  ICChannel,
  ICMessage,
  ICUser,
  fetchICUsers,
  sendVoiceMessage,
  subscribeToChannelMessages,
  subscribeToChannels,
  subscribeToDirectConversation,
} from '../../services/instantConnectService';
import { getNotificationOnly, setNotificationOnly } from './InstantConnectListener';

interface InstantConnectModalProps {
  open: boolean;
  onClose: () => void;
}

type Mode = 'channel' | 'direct';

type RecordingTarget =
  | { type: 'channel'; channelId: string }
  | { type: 'direct'; recipientId: string; recipientName: string };

export const InstantConnectModal: React.FC<InstantConnectModalProps> = ({ open, onClose }) => {
  const { currentUser } = useAuth();
  const [mode, setMode] = useState<Mode>('channel');
  const [channels, setChannels] = useState<ICChannel[]>([]);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ICMessage[]>([]);
  const [users, setUsers] = useState<ICUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<ICUser | null>(null);
  const [userSearch, setUserSearch] = useState('');
  const [recording, setRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [sending, setSending] = useState(false);
  const [notificationOnly, setNotificationOnlyState] = useState(getNotificationOnly);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const recordingTargetRef = useRef<RecordingTarget | null>(null);

  useEffect(() => {
    if (!open) return;
    const unsub = subscribeToChannels(setChannels);
    return () => unsub();
  }, [open]);

  useEffect(() => {
    if (channels.length > 0 && !selectedChannelId) {
      setSelectedChannelId(channels[0].id);
    }
  }, [channels, selectedChannelId]);

  useEffect(() => {
    if (!open) return;
    fetchICUsers().then(setUsers).catch(() => setUsers([]));
  }, [open]);

  useEffect(() => {
    if (!open || !currentUser) return;
    if (mode === 'channel' && selectedChannelId) {
      const unsub = subscribeToChannelMessages(selectedChannelId, setMessages);
      return () => unsub();
    }
    if (mode === 'direct' && currentUser && selectedUser) {
      const unsub = subscribeToDirectConversation(currentUser.uid, selectedUser.uid, setMessages);
      return () => unsub();
    }
    if (mode === 'direct') {
      setMessages([]);
    }
  }, [open, mode, selectedChannelId, currentUser, selectedUser]);

  const filteredUsers = useMemo(() => {
    if (!userSearch.trim()) return users.filter((u) => u.uid !== currentUser?.uid);
    const lower = userSearch.toLowerCase();
    return users
      .filter((u) => u.uid !== currentUser?.uid)
      .filter((u) => u.displayName.toLowerCase().includes(lower) || u.email.toLowerCase().includes(lower));
  }, [users, userSearch, currentUser]);

  const pickRecorderMime = (): string | undefined => {
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
    return candidates.find((t) => MediaRecorder.isTypeSupported(t));
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickRecorderMime();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.start();
      setRecording(true);
      setRecordingDuration(0);
      timerRef.current = setInterval(() => {
        setRecordingDuration((d) => d + 1);
      }, 1000);
    } catch {
      recordingTargetRef.current = null;
      toast.error('Microphone access denied');
    }
  }, []);

  const stopRecording = useCallback(async () => {
    if (!mediaRecorderRef.current || !currentUser) return;

    return new Promise<void>((resolve) => {
      const recorder = mediaRecorderRef.current!;
      recorder.onstop = async () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        setRecording(false);

        const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size === 0) {
          recordingTargetRef.current = null;
          resolve();
          return;
        }

        const target = recordingTargetRef.current;
        recordingTargetRef.current = null;
        if (!target) {
          resolve();
          return;
        }

        setSending(true);
        try {
          await sendVoiceMessage({
            senderId: currentUser.uid,
            senderName: currentUser.displayName,
            senderEmail: currentUser.email,
            audioBlob: blob,
            duration: recordingDuration,
            ...(target.type === 'channel'
              ? { channelId: target.channelId }
              : { recipientId: target.recipientId, recipientName: target.recipientName }),
          });
          toast.success(target.type === 'direct' ? `Sent to ${target.recipientName}` : 'Message sent');
        } catch (err) {
          console.error('[InstantConnect] send failed:', err);
          const msg = err instanceof Error ? err.message : 'Failed to send message';
          toast.error(msg.includes('permission') || msg.includes('Permission')
            ? 'Permission denied — check Instant Connect access'
            : 'Failed to send message');
        } finally {
          setSending(false);
        }
        resolve();
      };
      recorder.stop();
    });
  }, [currentUser, recordingDuration]);

  const handlePTTDown = useCallback(() => {
    if (recording || sending) return;
    if (mode === 'direct' && !selectedUser) {
      toast.error('Select a user first');
      return;
    }
    if (mode === 'channel' && !selectedChannelId) {
      toast.error('No channel selected');
      return;
    }

    recordingTargetRef.current = mode === 'channel'
      ? { type: 'channel', channelId: selectedChannelId! }
      : { type: 'direct', recipientId: selectedUser!.uid, recipientName: selectedUser!.displayName };

    startRecording();
  }, [recording, sending, mode, selectedUser, selectedChannelId, startRecording]);

  const handlePTTUp = useCallback(() => {
    if (!recording) return;
    stopRecording();
  }, [recording, stopRecording]);

  const toggleNotificationOnly = () => {
    const next = !notificationOnly;
    setNotificationOnlyState(next);
    setNotificationOnly(next);
    toast.success(next ? 'Notification-only mode on' : 'Auto-play mode on');
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  if (!open) return null;

  const channelName = channels.find((c) => c.id === selectedChannelId)?.name || 'All';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-md bg-white border border-gray-200 rounded-xl shadow-xl flex flex-col overflow-hidden max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50/80">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Radio className="w-4 h-4 text-gray-700" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Instant Connect</h2>
              <p className="text-xs text-gray-500">Push-to-talk intercom</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={toggleNotificationOnly}
              title={notificationOnly ? 'Notification only — click to auto-play' : 'Auto-play — click for notifications only'}
              className={`p-2 rounded-lg transition-colors ${
                notificationOnly
                  ? 'text-amber-600 bg-amber-50 hover:bg-amber-100'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
            >
              {notificationOnly ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Mode toggle */}
        <div className="flex px-5 pt-4 gap-2">
          <button
            onClick={() => setMode('channel')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors border ${
              mode === 'channel'
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Radio className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
            Channel
          </button>
          <button
            onClick={() => setMode('direct')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors border ${
              mode === 'direct'
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <User className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
            Direct
          </button>
        </div>

        {/* Channel selector / User search */}
        <div className="px-5 pt-3">
          {mode === 'channel' ? (
            <select
              value={selectedChannelId || ''}
              onChange={(e) => setSelectedChannelId(e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400"
            >
              {channels.length === 0 && <option value="">No channels available</option>}
              {channels.map((ch) => (
                <option key={ch.id} value={ch.id}>
                  {ch.name}
                </option>
              ))}
            </select>
          ) : (
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-400"
                />
              </div>
              {selectedUser ? (
                <div className="flex items-center gap-2 bg-gray-100 border border-gray-200 rounded-lg px-3 py-2">
                  <User className="w-4 h-4 text-gray-600" />
                  <span className="text-sm text-gray-800 flex-1 font-medium">{selectedUser.displayName}</span>
                  <button onClick={() => setSelectedUser(null)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="max-h-32 overflow-y-auto space-y-0.5 rounded-lg border border-gray-100 bg-gray-50/50 p-1">
                  {filteredUsers.map((u) => (
                    <button
                      key={u.uid}
                      onClick={() => {
                        setSelectedUser(u);
                        setUserSearch('');
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm text-gray-700 hover:bg-white hover:shadow-sm transition"
                    >
                      {u.photoURL ? (
                        <img src={u.photoURL} alt="" className="w-6 h-6 rounded-full object-cover" />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-500 font-medium">
                          {u.displayName.charAt(0)}
                        </div>
                      )}
                      <span>{u.displayName}</span>
                    </button>
                  ))}
                  {filteredUsers.length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-3">No users found</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Push-to-talk button */}
        <div className="flex flex-col items-center py-6 px-5">
          <button
            onMouseDown={handlePTTDown}
            onMouseUp={handlePTTUp}
            onMouseLeave={() => { if (recording) stopRecording(); }}
            onTouchStart={handlePTTDown}
            onTouchEnd={handlePTTUp}
            disabled={sending}
            className={`w-24 h-24 rounded-full flex items-center justify-center transition-all select-none shadow-lg ${
              recording
                ? 'bg-red-500 scale-110 shadow-red-200 animate-pulse'
                : sending
                ? 'bg-gray-300 cursor-wait shadow-gray-200'
                : 'bg-gray-800 hover:bg-gray-700 active:scale-95 shadow-gray-300'
            }`}
          >
            {sending ? (
              <Send className="w-8 h-8 text-white animate-bounce" />
            ) : (
              <Mic className="w-8 h-8 text-white" />
            )}
          </button>
          <p className="mt-3 text-sm text-gray-600 font-medium">
            {recording
              ? `Recording… ${recordingDuration}s`
              : sending
              ? 'Sending…'
              : 'Hold to talk'}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {mode === 'channel' ? `Channel: ${channelName}` : selectedUser ? `To: ${selectedUser.displayName}` : 'Select a user above'}
          </p>
        </div>

        {/* Message history */}
        <div className="flex-1 overflow-y-auto border-t border-gray-200 px-5 py-3 min-h-0 bg-gray-50/30">
          <p className="text-[11px] text-gray-400 mb-2 font-semibold uppercase tracking-wider">Recent Messages</p>
          {messages.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No messages yet</p>
          ) : (
            <div className="space-y-2">
              {messages.map((msg) => (
                <MessageBubble key={msg.id} message={msg} isMine={msg.senderId === currentUser?.uid} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MessageBubble: React.FC<{ message: ICMessage; isMine: boolean }> = ({ message, isMine }) => {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlay = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(message.audioUrl);
      audioRef.current.onended = () => setPlaying(false);
      audioRef.current.onerror = () => setPlaying(false);
    }
    if (playing) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
    } else {
      audioRef.current.play().catch(() => setPlaying(false));
      setPlaying(true);
    }
  };

  const time = message.timestamp
    ? new Date(message.timestamp.toMillis()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <div className={`flex items-start gap-2 ${isMine ? 'flex-row-reverse' : ''}`}>
      <div
        className={`rounded-xl px-3 py-2.5 max-w-[75%] cursor-pointer transition-all ${
          isMine
            ? 'bg-gray-100 border border-gray-300'
            : 'bg-white border border-gray-200 shadow-sm'
        } ${playing ? 'ring-2 ring-gray-400' : 'hover:shadow-md'}`}
        onClick={handlePlay}
      >
        <div className="flex items-center gap-2.5">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
            playing ? 'bg-gray-700 animate-pulse' : isMine ? 'bg-gray-200' : 'bg-gray-100'
          }`}>
            <Mic className={`w-3.5 h-3.5 ${playing ? 'text-white' : isMine ? 'text-gray-700' : 'text-gray-500'}`} />
          </div>
          <div className="flex flex-col">
            <span className="text-xs text-gray-700 font-medium leading-tight">{isMine ? 'You' : message.senderName}</span>
            <span className="text-[10px] text-gray-400">{time} · {message.duration}s</span>
          </div>
        </div>
      </div>
    </div>
  );
};
